import { useEffect, useRef, useState } from "react";
import { NavLink } from "react-router-dom";
import { db } from "../FIREBASE/firebase";
import routeUrls from "../COMPONENTS/navbar/navLink";
import { fetchRollerRoute, analyzeRoute, RouteResult, RoutePoint, SEG_COLOR, PROFILES } from "../COMPONENTS/rollerski/brouter";
import "../CSS/rollerski.scss";
import { usePageBackground } from "../COMPONENTS/usePageBackground";
import NumberInput from "../COMPONENTS/NumberInput";

// MapLibre GL from CDN (same reason as TripMap: CRA/webpack 4 chokes on its dist).
const VER = "4.7.1";
const JS = `https://unpkg.com/maplibre-gl@${VER}/dist/maplibre-gl.js`;
const CSS = `https://unpkg.com/maplibre-gl@${VER}/dist/maplibre-gl.css`;
let mlPromise: Promise<any> | null = null;
const loadMapLibre = (): Promise<any> => {
  const w = window as any;
  if (w.maplibregl) return Promise.resolve(w.maplibregl);
  if (mlPromise) return mlPromise;
  mlPromise = new Promise((resolve, reject) => {
    if (!document.getElementById("maplibre-css")) {
      const link = document.createElement("link");
      link.id = "maplibre-css"; link.rel = "stylesheet"; link.href = CSS;
      document.head.appendChild(link);
    }
    const s = document.createElement("script");
    s.src = JS; s.async = true;
    s.onload = () => resolve((window as any).maplibregl);
    s.onerror = () => reject(new Error("Kunde inte ladda kartan"));
    document.head.appendChild(s);
  });
  return mlPromise;
};

const OSM_STYLE = {
  version: 8 as const,
  sources: { osm: { type: "raster", tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"], tileSize: 256, attribution: "© OpenStreetMap" } },
  layers: [{ id: "osm", type: "raster", source: "osm" }],
};

interface Waypoint { id: string; lat: number; lng: number; }
const newId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 7);

// Parse a GPX file's track/route points with elevation (prefers <rtept>, else <trkpt>).
const parseGpx = (text: string): RoutePoint[] => {
  const doc = new DOMParser().parseFromString(text, "application/xml");
  const rtepts = Array.from(doc.getElementsByTagName("rtept"));
  const nodes = rtepts.length ? rtepts : Array.from(doc.getElementsByTagName("trkpt"));
  return nodes
    .map((n) => {
      const eleEl = n.getElementsByTagName("ele")[0];
      return {
        lat: parseFloat(n.getAttribute("lat") || ""),
        lng: parseFloat(n.getAttribute("lon") || ""),
        ele: eleEl ? parseFloat(eleEl.textContent || "0") || 0 : 0,
      };
    })
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
};
// Thin a dense track to at most `max` evenly-spaced control points.
const downsample = <T,>(pts: T[], max: number): T[] => {
  if (pts.length <= max) return pts;
  const step = (pts.length - 1) / (max - 1);
  const out: T[] = [];
  for (let i = 0; i < max; i++) out.push(pts[Math.round(i * step)]);
  return out;
};
const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

export const RollerSkiView = ({ uid }: { uid: string }) => {
  usePageBackground();
  const [waypoints, setWaypoints] = useState<Waypoint[]>([]);
  const [maxDown, setMaxDown] = useState(6);
  const [minSteep, setMinSteep] = useState(30); // steep hills shorter than this are OK (m)
  const [profile, setProfile] = useState("fastbike");
  const [avoidHills, setAvoidHills] = useState(true);
  const [showMarkers, setShowMarkers] = useState(true);
  const [addMode, setAddMode] = useState(true); // click map adds a waypoint
  const [importedPts, setImportedPts] = useState<RoutePoint[] | null>(null); // exact GPX track
  const [hydrated, setHydrated] = useState(false);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [route, setRoute] = useState<RouteResult | null>(null);

  const mapEl = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);
  const mlRef = useRef<any>(null);
  const markers = useRef<Record<string, any>>({});
  const steepMarkers = useRef<any[]>([]);
  const dangerMarkers = useRef<any[]>([]);
  const wpRef = useRef<Waypoint[]>([]);
  wpRef.current = waypoints;
  const addModeRef = useRef(addMode);
  addModeRef.current = addMode;

  // load persisted waypoints/settings
  useEffect(() => {
    let cancelled = false;
    db.collection("users").doc(uid).get().then((snap) => {
      if (cancelled) return;
      const r = snap.data()?.rollerski;
      if (r) {
        setWaypoints(Array.isArray(r.waypoints) ? r.waypoints.map((w: any) => ({ id: w?.id ?? newId(), lat: Number(w.lat), lng: Number(w.lng) })) : []);
        if (typeof r.maxDown === "number") setMaxDown(r.maxDown);
        if (typeof r.minSteep === "number") setMinSteep(r.minSteep);
        if (typeof r.profile === "string") setProfile(r.profile);
        if (typeof r.avoidHills === "boolean") setAvoidHills(r.avoidHills);
        if (typeof r.showMarkers === "boolean") setShowMarkers(r.showMarkers);
      }
      setHydrated(true);
    }).catch(() => setHydrated(true));
    return () => { cancelled = true; };
  }, [uid]);

  useEffect(() => {
    if (!hydrated) return;
    const t = window.setTimeout(() => {
      db.collection("users").doc(uid).set({ rollerski: { waypoints, maxDown, minSteep, profile, avoidHills, showMarkers } }, { merge: true }).catch(() => {});
    }, 700);
    return () => window.clearTimeout(t);
  }, [waypoints, maxDown, minSteep, profile, avoidHills, showMarkers, hydrated, uid]);

  // init map
  useEffect(() => {
    let cancelled = false;
    loadMapLibre().then((ml) => {
      if (cancelled || !mapEl.current) return;
      mlRef.current = ml;
      const map = new ml.Map({ container: mapEl.current, style: OSM_STYLE as any, center: [15, 62], zoom: 4 });
      mapRef.current = map;
      map.addControl(new ml.NavigationControl(), "top-right");
      map.on("load", () => {
        map.addSource("route", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({ id: "route-line", type: "line", source: "route", layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": ["get", "color"], "line-width": 5 } });
        setReady(true);
      });
      map.on("click", (e: any) => {
        if (!addModeRef.current) return; // pan/zoom mode — don't drop a waypoint
        setWaypoints((cur) => [...cur, { id: newId(), lat: e.lngLat.lat, lng: e.lngLat.lng }]);
      });
    }).catch((err) => setMsg(err.message));
    return () => { cancelled = true; mapRef.current?.remove(); mapRef.current = null; };
  }, []);

  // reconcile markers with waypoints
  useEffect(() => {
    const map = mapRef.current; const ml = mlRef.current;
    if (!map || !ml || !ready) return;
    const M = markers.current;
    Object.keys(M).forEach((id) => { if (!waypoints.find((w) => w.id === id)) { M[id].remove(); delete M[id]; } });
    waypoints.forEach((w, i) => {
      if (M[w.id]) {
        M[w.id].setLngLat([w.lng, w.lat]);
        M[w.id].getElement().textContent = String(i + 1);
        M[w.id].setDraggable(addMode); // lock dragging in pan/zoom mode
      } else {
        const el = document.createElement("div");
        el.className = "rs__marker";
        el.textContent = String(i + 1);
        el.title = "Dra för att flytta · klicka för att ta bort";
        const mk = new ml.Marker({ element: el, draggable: addMode }).setLngLat([w.lng, w.lat]).addTo(map);
        let dragged = false;
        mk.on("dragstart", () => { dragged = false; });
        mk.on("drag", () => { dragged = true; });
        mk.on("dragend", () => {
          const ll = mk.getLngLat();
          setWaypoints((cur) => cur.map((x) => (x.id === w.id ? { ...x, lat: ll.lat, lng: ll.lng } : x)));
        });
        el.addEventListener("click", (ev) => {
          if (dragged) { dragged = false; return; } // that was a move, not a tap
          if (!addModeRef.current) return;           // only remove in add/edit mode
          ev.stopPropagation();
          setWaypoints((cur) => cur.filter((x) => x.id !== w.id));
          setRoute(null);
        });
        M[w.id] = mk;
      }
    });
  }, [waypoints, ready, addMode]);

  // crosshair cursor only in add-waypoint mode
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    map.getCanvas().style.cursor = addMode ? "crosshair" : "";
  }, [addMode, ready]);

  // draw route
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !ready) return;
    const src = map.getSource("route");
    if (!src) return;
    const features = (route?.segments || []).map((s) => ({
      type: "Feature",
      properties: { color: SEG_COLOR[s.status] },
      geometry: { type: "LineString", coordinates: [[s.a.lng, s.a.lat], [s.b.lng, s.b.lat]] },
    }));
    src.setData({ type: "FeatureCollection", features });
  }, [route, ready]);

  // place a warning marker at each steep downhill (start of the section)
  useEffect(() => {
    const map = mapRef.current; const ml = mlRef.current;
    if (!map || !ml || !ready) return;
    steepMarkers.current.forEach((m) => m.remove());
    steepMarkers.current = [];
    if (!showMarkers) return;
    (route?.steepSpots || []).forEach((s) => {
      const pct = Math.abs(Math.round(s.maxGrade));
      const len = Math.round(s.lengthM);
      const drop = Math.round(s.dropM);
      const el = document.createElement("div");
      el.className = "rs__steep";
      el.textContent = `⚠ ${pct}%`;
      el.title = `Brant nedförsbacke – klicka för längd`;
      const mk = new ml.Marker({ element: el, anchor: "bottom" }).setLngLat([s.lng, s.lat]).addTo(map);
      mk.setPopup(new ml.Popup({ offset: 16 }).setHTML(
        `<b>Brant nedförsbacke</b><br>${pct}% brantast<br>${len} m lång<br>${drop} m fall`
      ));
      steepMarkers.current.push(mk);
    });
  }, [route, ready, showMarkers]);

  // mark sharp turns right after a descent — the key hazard
  useEffect(() => {
    const map = mapRef.current; const ml = mlRef.current;
    if (!map || !ml || !ready) return;
    dangerMarkers.current.forEach((m) => m.remove());
    dangerMarkers.current = [];
    if (!showMarkers) return;
    (route?.dangerSpots || []).forEach((s) => {
      const pct = Math.abs(Math.round(s.grade));
      const el = document.createElement("div");
      el.className = "rs__danger";
      const html = s.kind === "crossing"
        ? `<b>Övergångsställe/signal</b><br>efter ${pct}% nedförsbacke<br>svårt att stanna!`
        : `<b>Tvär sväng (${s.turn}°)</b><br>efter ${pct}% nedförsbacke<br>bromssträcka!`;
      el.textContent = s.kind === "crossing" ? "🚸" : "⛔";
      el.title = s.kind === "crossing" ? "Övergångsställe efter nedförsbacke — klicka för info" : "Tvär sväng efter nedförsbacke — klicka för info";
      const mk = new ml.Marker({ element: el, anchor: "bottom" }).setLngLat([s.lng, s.lat]).addTo(map);
      mk.setPopup(new ml.Popup({ offset: 16 }).setHTML(html));
      dangerMarkers.current.push(mk);
    });
  }, [route, ready, showMarkers]);

  const removeWp = (id: string) => { setWaypoints((cur) => cur.filter((w) => w.id !== id)); setRoute(null); };
  const clearAll = () => { setWaypoints([]); setRoute(null); setMsg(""); setImportedPts(null); };

  const onImportGpx = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      const pts = parseGpx(await file.text());
      if (pts.length < 2) { setMsg("Hittade inga punkter i GPX-filen."); return; }
      // Show the GPX exactly, but place draggable control-point waypoints along it
      // (≈1/km, 5–25) so it can be adjusted or extended, then re-routed on Beräkna.
      setImportedPts(pts);
      const km = analyzeRoute(pts, maxDown).lengthKm;
      const n = clamp(Math.round(km), 5, 25);
      setWaypoints(downsample(pts, n).map((p) => ({ id: newId(), lat: p.lat, lng: p.lng })));
      const map = mapRef.current, ml = mlRef.current;
      if (map && ml) { const b = new ml.LngLatBounds(); pts.forEach((p) => b.extend([p.lng, p.lat])); map.fitBounds(b, { padding: 60, duration: 600 }); }
      const hasEle = pts.some((p) => p.ele !== 0);
      setMsg(`Importerade GPX (${pts.length} punkter)${hasEle ? "" : " — utan höjddata går backanalysen inte att göra"}. Dra waypoints för att justera, lägg till i änden för att förlänga, tryck 🛼 Beräkna rutt.`);
    } catch {
      setMsg("Kunde inte läsa GPX-filen.");
    }
  };

  // Show the exact imported GPX track (with its draggable waypoints on top)
  // until the user re-routes with Beräkna, which clears importedPts. Re-asserts
  // on waypoint edits so moving/removing a control point doesn't blank the track.
  useEffect(() => {
    if (importedPts) setRoute(analyzeRoute(importedPts, maxDown, [], [], minSteep));
  }, [importedPts, maxDown, minSteep, waypoints]);

  const exportGpx = () => {
    if (!route || !route.points.length) return;
    const trkpts = route.points
      .map((p) => `    <trkpt lat="${p.lat.toFixed(6)}" lon="${p.lng.toFixed(6)}"><ele>${p.ele.toFixed(1)}</ele></trkpt>`)
      .join("\n");
    const gpx = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="sebastiangranlund.com rullskidor" xmlns="http://www.topografix.com/GPX/1/1">
  <trk><name>Rullskidrutt</name><trkseg>
${trkpts}
  </trkseg></trk>
</gpx>`;
    const url = URL.createObjectURL(new Blob([gpx], { type: "application/gpx+xml" }));
    const a = document.createElement("a");
    a.href = url; a.download = "rullskidrutt.gpx"; a.click();
    URL.revokeObjectURL(url);
  };

  const problemScore = (r: RouteResult) => r.steepSections + r.sharpTurns + r.crossingHazards + (r.gravelKm > 0.05 ? 1 : 0);

  // Route pts via BRouter, optionally re-routing around steep/danger spots.
  const routeWithAvoid = async (pts: { lat: number; lng: number }[]): Promise<RouteResult> => {
    let r = await fetchRollerRoute(pts, maxDown, profile, [], minSteep);
    if (avoidHills) {
      const nogos: { lat: number; lng: number; r: number }[] = [];
      for (let pass = 0; pass < 2 && problemScore(r) > 0; pass++) {
        const spots = [...r.steepSpots, ...r.dangerSpots];
        if (spots.length === 0) break;
        spots.forEach((s) => nogos.push({ lat: s.lat, lng: s.lng, r: 45 }));
        try {
          const r2 = await fetchRollerRoute(pts, maxDown, profile, nogos, minSteep);
          if (problemScore(r2) < problemScore(r)) r = r2; else break;
        } catch { break; }
      }
    }
    return r;
  };

  const fitTo = (pts: { lat: number; lng: number }[]) => {
    const map = mapRef.current, ml = mlRef.current;
    if (map && ml && pts.length) {
      const b = new ml.LngLatBounds();
      pts.forEach((p) => b.extend([p.lng, p.lat]));
      map.fitBounds(b, { padding: 60, duration: 600 });
    }
  };

  const compute = async () => {
    setBusy(true); setMsg(""); setImportedPts(null);
    try {
      const r = await routeWithAvoid(waypoints);
      setRoute(r);
      fitTo(r.points);
      if (problemScore(r) === 0 && r.motorwayKm < 0.05) setMsg("✓ Rutten ser rullskidevänlig ut!");
      else if (avoidHills) setMsg("Försökte rutta runt backarna — kvarvarande problem visas nedan.");
    } catch (e: any) {
      setMsg(e?.message || "Kunde inte beräkna rutt.");
      setRoute(null);
    }
    setBusy(false);
  };

  return (
    <div className="rs">
      <div className="rs__header">
        <NavLink to={routeUrls.PROJEKT} className="rs__back">← Projekt</NavLink>
        <h1 className="rs__title">Rullskidor</h1>
        <span className="rs__sub">Sätt ut waypoints — hitta asfaltrutter utan för branta nedförsbackar</span>
      </div>

      <div className="rs__layout">
        <div className="rs__mapwrap">
          <div ref={mapEl} className="rs__map" />
          <div className="rs__hint">{addMode ? "Klicka på kartan för att lägga till en waypoint. Dra en punkt för att flytta." : "Panorera/zooma fritt — klick lägger inte till waypoints. Slå på \"Lägg till waypoints\" för att placera nya."}</div>
        </div>

        <div className="rs__panel">
          <div className="rs__row">
            <label className="rs__field">
              <span>Max nedför (%)</span>
              <NumberInput className="rs__input" min={1} max={30} value={maxDown} onChange={setMaxDown} />
            </label>
            <label className="rs__field">
              <span>Kort backe OK (m)</span>
              <NumberInput className="rs__input" min={0} max={500} step={5} value={minSteep} onChange={setMinSteep} title="Branta backar kortare än detta flaggas inte" />
            </label>
            <label className="rs__field">
              <span>Profil</span>
              <select className="rs__input" value={profile} onChange={(e) => setProfile(e.target.value)}>
                {PROFILES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
              </select>
            </label>
          </div>

          <label className="rs__toggle">
            <input type="checkbox" checked={avoidHills} onChange={(e) => setAvoidHills(e.target.checked)} />
            <span>Försök rutta runt branta backar &amp; tvära svängar</span>
          </label>
          <label className="rs__toggle">
            <input type="checkbox" checked={addMode} onChange={(e) => setAddMode(e.target.checked)} />
            <span>Lägg till waypoints vid klick (av = panorera/zooma fritt)</span>
          </label>
          <label className="rs__toggle">
            <input type="checkbox" checked={showMarkers} onChange={(e) => setShowMarkers(e.target.checked)} />
            <span>Visa markörer (klicka en backe för längd)</span>
          </label>

          <div className="rs__actions">
            <button className="rs__btn" onClick={compute} disabled={busy || waypoints.length < 2}>{busy ? "Beräknar…" : "🛼 Beräkna rutt"}</button>
            {waypoints.length > 0 && <button className="rs__btn rs__btn--ghost" onClick={clearAll}>Rensa</button>}
          </div>
          <div className="rs__actions">
            <label className="rs__btn rs__btn--ghost">📥 Importera GPX<input type="file" accept=".gpx,application/gpx+xml,text/xml" hidden onChange={onImportGpx} /></label>
            {route && <button className="rs__btn rs__btn--ghost" onClick={exportGpx}>💾 Spara GPX</button>}
          </div>
          {msg && <p className="rs__msg">{msg}</p>}

          <div className="rs__wps">
            {waypoints.length === 0 && <p className="rs__empty">Inga waypoints ännu — klicka på kartan.</p>}
            {waypoints.map((w, i) => (
              <div key={w.id} className="rs__wp">
                <span className="rs__wp-no">{i + 1}</span>
                <span className="rs__wp-coord">{w.lat.toFixed(4)}, {w.lng.toFixed(4)}</span>
                <button className="rs__wp-del" onClick={() => removeWp(w.id)} aria-label="Ta bort">×</button>
              </div>
            ))}
          </div>

          {route && (
            <div className="rs__result">
              <div className="rs__stats">
                <span><b>{route.lengthKm.toFixed(1)}</b> km</span>
                <span>↗ <b>{route.ascentM}</b> m</span>
                <span>↘ <b>{route.descentM}</b> m</span>
              </div>
              <ul className="rs__flags">
                <li className={route.sharpTurns ? "rs__flag rs__flag--bad" : "rs__flag rs__flag--ok"}>
                  <span className="rs__dot" style={{ background: "#c92a2a" }} />
                  {route.sharpTurns ? `${route.sharpTurns} tvär${route.sharpTurns > 1 ? "a" : ""} sväng${route.sharpTurns > 1 ? "ar" : ""} efter nedförsbacke ⛔` : "Inga tvära svängar efter nedförsbackar"}
                </li>
                {route.crossingHazards > 0 && (
                  <li className="rs__flag rs__flag--bad">
                    <span className="rs__dot" style={{ background: "#c92a2a" }} />
                    {route.crossingHazards} övergångsställe{route.crossingHazards > 1 ? "n" : ""}/signal efter nedförsbacke 🚸
                  </li>
                )}
                <li className={route.steepSections ? "rs__flag rs__flag--bad" : "rs__flag rs__flag--ok"}>
                  <span className="rs__dot" style={{ background: SEG_COLOR.steep }} />
                  {route.steepSections ? `${route.steepSections} branta nedförsbackar (> ${maxDown}%)` : "Inga för branta nedförsbackar"}
                </li>
                <li className={route.gravelKm > 0.05 ? "rs__flag rs__flag--bad" : "rs__flag rs__flag--ok"}>
                  <span className="rs__dot" style={{ background: SEG_COLOR.surface }} />
                  {route.gravelKm > 0.05 ? `${route.gravelKm.toFixed(1)} km ej asfalt/grus` : "Asfalt hela vägen"}
                </li>
                {route.motorwayKm > 0.05 && (
                  <li className="rs__flag rs__flag--bad">
                    <span className="rs__dot" style={{ background: SEG_COLOR.motorway }} />
                    {route.motorwayKm.toFixed(1)} km motorväg/motortrafikled
                  </li>
                )}
              </ul>
              {(route.sharpTurns > 0 || route.crossingHazards > 0) && (
                <p className="rs__hint">⛔/🚸 markerar en tvär sväng eller ett övergångsställe strax efter en nedförsbacke — svårt att bromsa på rullskidor. Överväg en annan väg där.</p>
              )}
              {route.steepSections > 0 && (
                <p className="rs__hint">Röda partier på kartan är för branta nedför. Flytta en waypoint förbi backen och beräkna om.</p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
